import { cn } from '@/lib/utils';

interface DashboardSkeletonProps {
  isMobile: boolean;
  showScopeSelector?: boolean;
}

interface MobileAwareProps {
  isMobile: boolean;
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-muted/30', className)} />;
}

function GreetingSkeleton({ isMobile, showScopeSelector = false }: DashboardSkeletonProps) {
  return (
    <div className={cn(isMobile ? 'space-y-3' : 'flex items-center justify-between gap-3')}>
      <div className="flex items-center gap-3">
        <SkeletonBlock className="h-9 w-9 shrink-0 rounded-full" />
        <div className="space-y-2">
          <SkeletonBlock className="h-5 w-48 rounded-md" />
          <SkeletonBlock className="h-3 w-32 rounded-md" />
        </div>
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <SkeletonBlock className="h-9 w-24 rounded-full" />
        {showScopeSelector && <SkeletonBlock className="h-9 w-28 rounded-full" />}
        <SkeletonBlock className="h-9 w-32 rounded-full" />
      </div>
    </div>
  );
}

function ChartSkeleton() {
  const barHeights = ['h-16', 'h-24', 'h-20', 'h-28', 'h-36', 'h-32', 'h-40'];

  return (
    <div className="glass-card rounded-xl p-5">
      <SkeletonBlock className="h-4 w-20 rounded-md" />
      <div className="relative mt-4 h-52 overflow-hidden rounded-lg border border-white/[0.04] bg-white/[0.02] p-4">
        <SkeletonBlock className="absolute bottom-4 left-4 top-4 w-px rounded-full" />
        <SkeletonBlock className="absolute bottom-4 left-4 right-4 h-px rounded-full" />
        <div className="absolute inset-x-8 bottom-8 flex items-end gap-3">
          {barHeights.map((height, index) => (
            <SkeletonBlock key={index} className={cn('flex-1 rounded-md', height)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricTileSkeleton({ isMobile }: MobileAwareProps) {
  return (
    <div className={cn('glass-card rounded-xl', isMobile ? 'p-3.5' : 'p-5')}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <SkeletonBlock className={cn('rounded-md', isMobile ? 'h-3 w-20' : 'h-4 w-24')} />
        {!isMobile && <SkeletonBlock className="h-6 w-20 rounded-md" />}
      </div>
      <div className="flex items-baseline gap-2">
        <SkeletonBlock className={cn('rounded-md', isMobile ? 'h-7 w-14' : 'h-8 w-16')} />
        <SkeletonBlock className="h-3 w-10 rounded-md" />
      </div>
      {!isMobile && <SkeletonBlock className="mt-2 h-3 w-28 rounded-md" />}
    </div>
  );
}

function WhatsNewSkeleton() {
  return (
    <div className="relative overflow-hidden rounded-2xl">
      <div className="relative glass-card rounded-2xl p-5 space-y-3">
        <SkeletonBlock className="h-8 w-28 rounded-full" />
        <div className="space-y-2">
          <SkeletonBlock className="h-4 w-full rounded-md" />
          <SkeletonBlock className="h-4 w-11/12 rounded-md" />
          <SkeletonBlock className="h-4 w-3/4 rounded-md" />
        </div>
        <SkeletonBlock className="h-3 w-24 rounded-md" />
      </div>
    </div>
  );
}

function ActivityTableSkeleton({ isMobile }: MobileAwareProps) {
  return (
    <div className="glass-card rounded-xl p-5">
      <SkeletonBlock className="h-4 w-36 rounded-md" />

      {!isMobile && (
        <div className="hidden items-center px-3 pb-2 pt-3 md:flex">
          <SkeletonBlock className="h-3 flex-1 rounded-md" />
          <SkeletonBlock className="ml-6 h-3 w-8 rounded-md" />
          <SkeletonBlock className="ml-4 h-3 w-10 rounded-md" />
          <SkeletonBlock className="ml-4 h-3 w-10 rounded-md" />
        </div>
      )}

      <div className="mt-2 space-y-2">
        {Array.from({ length: 8 }).map((_, index) => (
          isMobile ? (
            <div key={index} className="rounded-lg bg-white/[0.02] p-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <SkeletonBlock className="h-4 w-28 rounded-md" />
                <SkeletonBlock className="h-3 w-14 rounded-md" />
              </div>
              <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                {Array.from({ length: 3 }).map((__, metricIndex) => (
                  <div key={metricIndex} className="flex items-center gap-1.5">
                    <SkeletonBlock className="h-1.5 w-1.5 rounded-full" />
                    <SkeletonBlock className="h-3 w-8 rounded-md" />
                    <SkeletonBlock className="ml-auto h-3 w-6 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div key={index} className="flex items-center gap-4 rounded-lg px-3 py-2.5">
              <SkeletonBlock className="h-4 flex-1 rounded-md" />
              <SkeletonBlock className="h-3 w-8 rounded-md" />
              <SkeletonBlock className="h-3 w-8 rounded-md" />
              <SkeletonBlock className="h-3 w-10 rounded-md" />
            </div>
          )
        ))}
      </div>
    </div>
  );
}

export function DashboardSkeleton({ isMobile, showScopeSelector = false }: DashboardSkeletonProps) {
  return (
    <div className="flex flex-col gap-4 pt-4 md:flex-row md:gap-0">
      <div className="min-w-0 flex-1 space-y-3 overflow-hidden pr-0 md:space-y-5 md:pr-6">
        <GreetingSkeleton isMobile={isMobile} showScopeSelector={showScopeSelector} />
        <ChartSkeleton />

        <div className={cn('grid gap-3', isMobile ? 'grid-cols-2' : 'grid-cols-3')}>
          {Array.from({ length: 7 }).map((_, index) => (
            <MetricTileSkeleton key={index} isMobile={isMobile} />
          ))}
        </div>
      </div>

      <div className={cn(
        'shrink-0 space-y-4',
        isMobile
          ? 'w-full border-t border-white/[0.06] pt-4'
          : 'w-[360px] border-l border-white/[0.06] pl-6'
      )}>
        <WhatsNewSkeleton />
        <ActivityTableSkeleton isMobile={isMobile} />
      </div>
    </div>
  );
}
